import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchProducts } from '@/services/productService';
import { fetchCategories } from '@/services/categoryService';
import { Product } from '@/types/product';
import { DevisDraft, DevisLineItem } from '@/types/devis';
import {
  buildDevisHtml,
  computeDevisTotals,
  downloadDevisPdf,
  formatDevisDate,
  formatMoneyTn,
  formatSerialDisplay,
  getNextSerialSequence,
  getSuggestedSerial,
  saveDevisToHistory,
  setNextSerialSequence,
} from '@/services/devisService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Download,
  ImagePlus,
  LayoutGrid,
  Plus,
  Search,
  Settings2,
  Trash2,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface DevisGeneratorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a devis PDF is generated and saved to local history. */
  onDevisSaved?: () => void;
}

const STEPS = [
  { id: 'client', label: 'Client', subtitle: 'Identité & adresse' },
  { id: 'articles', label: 'Articles', subtitle: 'Sélection visuelle' },
  { id: 'details', label: 'Détails', subtitle: 'Quantités & prix' },
  { id: 'finalize', label: 'Aperçu', subtitle: 'PDF live & modifications' },
] as const;

type StepId = (typeof STEPS)[number]['id'];

const productImage = (product: Product): string => {
  if (product.image_nobg) return product.image_nobg;
  if (product.images?.length) return product.images[0];
  return '/placeholder.svg';
};

const lineImage = (item: DevisLineItem, products: Product[]): string => {
  if (item.image) return item.image;
  const product = item.product_id ? products.find((p) => p.id === item.product_id) : undefined;
  return product ? productImage(product) : '/placeholder.svg';
};

const lineTitle = (item: DevisLineItem, products: Product[]): string => {
  if (item.name?.trim()) return item.name.trim();
  if (item.isCustom) return item.designation.split('\n')[0] || 'Article perso';
  const product = item.product_id ? products.find((p) => p.id === item.product_id) : undefined;
  return product?.name || item.designation.split('\n')[0] || 'Article';
};

const buildProductDescription = (product: Product): string =>
  [
    product.description?.trim() || '',
    product.material?.name ? `Matériau: ${product.material.name}` : '',
    product.textile?.name ? `Tissu: ${product.textile.name}` : '',
  ]
    .filter(Boolean)
    .join('\n');

const buildDesignation = (product: Product): string =>
  [product.name, buildProductDescription(product)].filter(Boolean).join('\n');

const DevisGenerator: React.FC<DevisGeneratorProps> = ({ open, onOpenChange, onDevisSaved }) => {
  const { toast } = useToast();
  const [stepIndex, setStepIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  const [clientName, setClientName] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [clientMatricule, setClientMatricule] = useState('');
  const [serialNumber, setSerialNumber] = useState(getSuggestedSerial);
  const [date, setDate] = useState(formatDevisDate);
  const [discount, setDiscount] = useState(0);
  const [items, setItems] = useState<DevisLineItem[]>([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchOpen, setSearchOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [serialDialogOpen, setSerialDialogOpen] = useState(false);
  const [nextSerialInput, setNextSerialInput] = useState(String(getNextSerialSequence()));
  const [isDownloading, setIsDownloading] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customQty, setCustomQty] = useState(1);
  const [customPrice, setCustomPrice] = useState(0);
  const [customImage, setCustomImage] = useState<string>('');
  const [customImageName, setCustomImageName] = useState('');
  /** Mobile finalize: switch between edit panel and live PDF preview. */
  const [finalizeMobileTab, setFinalizeMobileTab] = useState<'edit' | 'preview'>('preview');

  const resetWizard = () => {
    setStepIndex(0);
    setDirection(1);
    setClientName('');
    setClientAddress('');
    setClientMatricule('');
    setSerialNumber(getSuggestedSerial());
    setDate(formatDevisDate());
    setDiscount(0);
    setItems([]);
    setSearchQuery('');
    setSelectedCategory('all');
    setSearchOpen(false);
    setCategoriesOpen(false);
    setIsDownloading(false);
    setCustomOpen(false);
    setCustomName('');
    setCustomQty(1);
    setCustomPrice(0);
    setCustomImage('');
    setCustomImageName('');
    setFinalizeMobileTab('preview');
  };

  const resetCustomForm = () => {
    setCustomName('');
    setCustomQty(1);
    setCustomPrice(0);
    setCustomImage('');
    setCustomImageName('');
  };

  useEffect(() => {
    if (!open) return;
    resetWizard();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const handleClose = () => onOpenChange(false);

  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ['admin', 'devis', 'products'],
    queryFn: () => fetchProducts(),
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['admin', 'devis', 'categories'],
    queryFn: fetchCategories,
  });

  const logoUrl = `${window.location.origin}/MKLogo-dark.png`;
  const step = STEPS[stepIndex];

  const draft: DevisDraft = useMemo(
    () => ({
      clientName: clientName.trim(),
      clientAddress: clientAddress.trim(),
      clientMatricule: clientMatricule.trim(),
      serialNumber: serialNumber.trim(),
      date,
      discount: Number(discount) || 0,
      items: items.filter((item) => item.designation.trim() && item.quantity > 0),
    }),
    [clientName, clientAddress, clientMatricule, serialNumber, date, discount, items]
  );

  const totals = useMemo(() => computeDevisTotals(draft), [draft]);
  const livePreviewHtml = useMemo(() => buildDevisHtml(draft, logoUrl), [draft, logoUrl]);

  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return products.filter((p) => {
      const matchesCategory =
        selectedCategory === 'all' ||
        p.category === selectedCategory ||
        p.category?.toLowerCase() === selectedCategory.toLowerCase();
      if (!matchesCategory) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q)
      );
    });
  }, [products, searchQuery, selectedCategory]);

  const selectedIds = useMemo(
    () => new Set(items.filter((i) => i.product_id).map((i) => i.product_id)),
    [items]
  );

  const toggleProduct = (product: Product) => {
    setItems((prev) => {
      const exists = prev.find((row) => row.product_id === product.id);
      if (exists) {
        return prev.filter((row) => row.product_id !== product.id);
      }
      return [
        ...prev,
        {
          id: crypto.randomUUID(),
          product_id: product.id,
          designation: buildDesignation(product),
          quantity: 1,
          unit_price: Number(product.price) || 0,
          name: product.name,
          description: buildProductDescription(product),
          image: productImage(product),
          isCustom: false,
        },
      ];
    });
  };

  const handleCustomImageChange = (file: File | null) => {
    if (!file) {
      setCustomImage('');
      setCustomImageName('');
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Image invalide', description: 'Choisissez un fichier image.', variant: 'destructive' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCustomImage(typeof reader.result === 'string' ? reader.result : '');
      setCustomImageName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleAddCustomArticle = () => {
    const name = customName.trim();
    if (!name) {
      toast({ title: 'Nom requis', description: 'Entrez le nom de l’article.', variant: 'destructive' });
      return;
    }
    if (!customImage) {
      toast({ title: 'Image requise', description: 'Ajoutez une image pour l’article perso.', variant: 'destructive' });
      return;
    }
    if (customQty < 1) {
      toast({ title: 'Quantité invalide', variant: 'destructive' });
      return;
    }

    setItems((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        product_id: '',
        name,
        description: '',
        designation: name,
        quantity: Math.max(1, Math.floor(customQty)),
        unit_price: Math.max(0, Number(customPrice) || 0),
        image: customImage,
        isCustom: true,
      },
    ]);
    resetCustomForm();
    setCustomOpen(false);
    toast({ title: 'Article perso ajouté', description: `${name} — uniquement pour ce devis.` });
  };

  const updateItem = (id: string, patch: Partial<DevisLineItem>) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  };

  const canGoNext = (): boolean => {
    if (step.id === 'client') return Boolean(clientName.trim() && clientAddress.trim());
    if (step.id === 'articles') return items.length > 0;
    if (step.id === 'details') return items.every((i) => i.quantity > 0 && i.unit_price >= 0);
    return Boolean(serialNumber.trim());
  };

  const goNext = () => {
    if (!canGoNext()) {
      if (step.id === 'client') {
        toast({
          title: 'Informations manquantes',
          description: 'Nom et adresse du client sont requis.',
          variant: 'destructive',
        });
      } else if (step.id === 'articles') {
        toast({
          title: 'Aucun article',
          description: 'Sélectionnez au moins un meuble.',
          variant: 'destructive',
        });
      }
      return;
    }
    if (stepIndex < STEPS.length - 1) {
      setDirection(1);
      setStepIndex((i) => i + 1);
    }
  };

  const goBack = () => {
    if (stepIndex > 0) {
      setDirection(-1);
      setStepIndex((i) => i - 1);
    }
  };

  const handleDownload = async () => {
    if (!canGoNext() || draft.items.length === 0) {
      toast({ title: 'Devis incomplet', variant: 'destructive' });
      return;
    }
    setIsDownloading(true);
    try {
      await downloadDevisPdf(draft, logoUrl);
      saveDevisToHistory(draft);
      onDevisSaved?.();
      setSerialNumber(getSuggestedSerial());
      toast({
        title: 'PDF téléchargé',
        description: `${formatSerialDisplay(draft.serialNumber)} généré.`,
      });
    } catch (error) {
      console.error(error);
      toast({
        title: 'Erreur PDF',
        description: 'Impossible de générer le PDF.',
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSaveSerialSettings = () => {
    const value = Number(nextSerialInput);
    if (!Number.isFinite(value) || value < 1) {
      toast({ title: 'Numéro invalide', variant: 'destructive' });
      return;
    }
    setNextSerialSequence(value);
    setSerialNumber(getSuggestedSerial());
    setSerialDialogOpen(false);
    toast({
      title: 'Compteur mis à jour',
      description: `Prochain : ${formatSerialDisplay(getSuggestedSerial())}`,
    });
  };

  const progress = ((stepIndex + 1) / STEPS.length) * 100;

  const slideVariants = {
    enter: (dir: number) => ({ x: dir > 0 ? 48 : -48, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir > 0 ? -48 : 48, opacity: 0 }),
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          key="devis-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[90] flex flex-col bg-black/40"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.985 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            className="flex h-[100dvh] w-screen min-h-0 flex-col overflow-hidden bg-gradient-to-b from-[#faf8f4] via-white to-[#f7f4ef] pt-[env(safe-area-inset-top)]"
            role="dialog"
            aria-modal="true"
            aria-label="Générer un devis"
          >
      {/* Top bar */}
      <header className="shrink-0 border-b border-border/60 bg-white/95 px-3 py-2.5 backdrop-blur sm:px-4 sm:py-3 md:px-6">
        <div className="mb-2 flex items-center justify-between gap-2 sm:mb-3 sm:gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-black">
              Nouveau devis
            </p>
            <h2 className="truncate font-sans text-base font-semibold tracking-wide text-foreground sm:text-lg md:text-xl">
              {step.label}
            </h2>
            <p className="hidden text-xs text-muted-foreground sm:block">{step.subtitle}</p>
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-11 w-11 text-muted-foreground sm:hidden"
              onClick={() => {
                setNextSerialInput(String(getNextSerialSequence()));
                setSerialDialogOpen(true);
              }}
              aria-label="Réglages série"
            >
              <Settings2 className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="hidden text-muted-foreground sm:inline-flex"
              onClick={() => {
                setNextSerialInput(String(getNextSerialSequence()));
                setSerialDialogOpen(true);
              }}
            >
              <Settings2 className="mr-2 h-4 w-4" />
              Série
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-11 w-11 text-muted-foreground hover:text-foreground"
              onClick={handleClose}
              aria-label="Fermer"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <div className="mb-2 h-1 overflow-hidden rounded-full bg-border/70 sm:mb-3">
          <motion.div
            className="h-full rounded-full bg-furniture-gold"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
          />
        </div>

        <ol className="grid grid-cols-4 gap-1">
          {STEPS.map((s, i) => {
            const done = i < stepIndex;
            const active = i === stepIndex;
            return (
              <li key={s.id} className="min-w-0">
                <button
                  type="button"
                  onClick={() => {
                    if (i <= stepIndex) {
                      setDirection(i < stepIndex ? -1 : 1);
                      setStepIndex(i);
                    }
                  }}
                  className={cn(
                    'flex w-full flex-col items-center gap-1 rounded-lg px-1 py-1.5 text-center transition-colors sm:flex-row sm:gap-2 sm:px-1.5 sm:text-left',
                    active && 'bg-furniture-gold/10',
                    done && 'hover:bg-muted/60'
                  )}
                >
                  <span
                    className={cn(
                      'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold sm:h-6 sm:w-6',
                      active && 'bg-furniture-gold text-black',
                      done && 'bg-foreground text-background',
                      !active && !done && 'bg-muted text-muted-foreground'
                    )}
                  >
                    {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
                  </span>
                  <span
                    className={cn(
                      'max-w-full truncate text-[9px] font-medium uppercase tracking-wider sm:text-[11px]',
                      active ? 'text-foreground' : 'text-muted-foreground'
                    )}
                  >
                    {s.label}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </header>

      {/* Step body */}
      <div className="relative min-h-0 flex-1 overflow-hidden">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step.id}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 flex min-h-0 flex-col overflow-hidden px-3 py-3 sm:px-4 sm:py-4 md:px-6 md:py-5"
          >
            {step.id === 'client' && (
              <div className="mx-auto flex h-full min-h-0 w-full max-w-2xl flex-col justify-start gap-4 overflow-y-auto overscroll-contain sm:justify-center sm:gap-5">
                <div className="space-y-1 text-center">
                  <h3 className="font-sans text-xl font-semibold tracking-wide sm:text-2xl">
                    Qui est le client ?
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Ces informations apparaîtront en en-tête du devis.
                  </p>
                </div>
                <div className="space-y-4 rounded-2xl border border-border/70 bg-white/90 p-4 shadow-soft sm:p-5 md:p-7">
                  <div className="space-y-2">
                    <Label htmlFor="clientName">Nom du client</Label>
                    <Input
                      id="clientName"
                      autoFocus
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="Mme Ons / M. …"
                      className="h-12 border-border/80 bg-background text-base"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="clientAddress">Adresse</Label>
                    <Textarea
                      id="clientAddress"
                      value={clientAddress}
                      onChange={(e) => setClientAddress(e.target.value)}
                      placeholder="Ville, rue…"
                      rows={3}
                      className="resize-none border-border/80 bg-background text-base"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="clientMatricule">Matricule F (optionnel)</Label>
                    <Input
                      id="clientMatricule"
                      value={clientMatricule}
                      onChange={(e) => setClientMatricule(e.target.value)}
                      placeholder="58 097 903"
                      className="h-11 border-border/80 bg-background"
                    />
                  </div>
                </div>
              </div>
            )}

            {step.id === 'articles' && (
              <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
                <div className="shrink-0 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setSearchOpen((v) => !v);
                          if (!searchOpen) setCategoriesOpen(false);
                        }}
                        className={cn(
                          'relative flex h-11 w-11 items-center justify-center rounded-full border shadow-soft transition-colors',
                          searchOpen || searchQuery
                            ? 'border-furniture-gold bg-furniture-gold text-black'
                            : 'border-border bg-white text-muted-foreground hover:border-furniture-gold/50 hover:text-foreground'
                        )}
                        aria-label="Rechercher"
                        aria-expanded={searchOpen}
                      >
                        <Search className="h-4 w-4" />
                        {searchQuery && !searchOpen && (
                          <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-furniture-gold ring-2 ring-white" />
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setCategoriesOpen((v) => !v);
                          if (!categoriesOpen) setSearchOpen(false);
                        }}
                        className={cn(
                          'relative flex h-11 w-11 items-center justify-center rounded-full border shadow-soft transition-colors',
                          categoriesOpen || selectedCategory !== 'all'
                            ? 'border-furniture-gold bg-furniture-gold text-black'
                            : 'border-border bg-white text-muted-foreground hover:border-furniture-gold/50 hover:text-foreground'
                        )}
                        aria-label="Catégories"
                        aria-expanded={categoriesOpen}
                      >
                        <LayoutGrid className="h-4 w-4" />
                        {selectedCategory !== 'all' && !categoriesOpen && (
                          <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-furniture-gold ring-2 ring-white" />
                        )}
                      </button>

                      <Button
                        type="button"
                        variant="outline"
                        className="h-11 rounded-full border-border bg-white px-3.5 shadow-soft"
                        onClick={() => {
                          resetCustomForm();
                          setCustomOpen(true);
                        }}
                      >
                        <Plus className="mr-1.5 h-4 w-4" />
                        Article perso
                      </Button>
                    </div>

                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>
                        {filteredProducts.length} article{filteredProducts.length !== 1 ? 's' : ''}
                      </span>
                      <span className="font-medium text-foreground">
                        {items.length} sélectionné{items.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  <AnimatePresence initial={false}>
                    {searchOpen && (
                      <motion.div
                        key="search-panel"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="relative pt-1">
                          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Rechercher un article par nom…"
                            className="h-12 border-border/80 bg-white pl-10 text-base shadow-soft"
                            autoFocus
                          />
                          {searchQuery && (
                            <button
                              type="button"
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              onClick={() => setSearchQuery('')}
                              aria-label="Effacer"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <AnimatePresence initial={false}>
                    {categoriesOpen && (
                      <motion.div
                        key="categories-panel"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="flex gap-2 overflow-x-auto pb-1 pt-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                          <button
                            type="button"
                            onClick={() => setSelectedCategory('all')}
                            className={cn(
                              'shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors',
                              selectedCategory === 'all'
                                ? 'border-furniture-gold bg-furniture-gold text-black'
                                : 'border-border bg-white text-muted-foreground hover:border-furniture-gold/50'
                            )}
                          >
                            Tous
                          </button>
                          {categories.map((cat) => (
                            <button
                              key={cat.id}
                              type="button"
                              onClick={() => setSelectedCategory(cat.name)}
                              className={cn(
                                'shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors',
                                selectedCategory === cat.name
                                  ? 'border-furniture-gold bg-furniture-gold text-black'
                                  : 'border-border bg-white text-muted-foreground hover:border-furniture-gold/50'
                              )}
                            >
                              {cat.name}
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                  {productsLoading ? (
                    <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
                      Chargement du catalogue…
                    </div>
                  ) : filteredProducts.length === 0 ? (
                    <div className="flex h-40 flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground">
                      <p>Aucun article trouvé.</p>
                      <p className="text-xs">Modifiez la recherche ou la catégorie.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7">
                      {filteredProducts.map((product) => {
                        const selected = selectedIds.has(product.id);
                        return (
                          <button
                            key={product.id}
                            type="button"
                            onClick={() => toggleProduct(product)}
                            className={cn(
                              'group relative overflow-hidden rounded-lg border bg-white text-left shadow-soft transition-all duration-300',
                              selected
                                ? 'border-furniture-gold ring-2 ring-furniture-gold/40'
                                : 'border-border/70 hover:-translate-y-0.5 hover:border-furniture-gold/40 hover:shadow-hover'
                            )}
                          >
                            <div className="relative aspect-[8/5] overflow-hidden bg-[#f3f1ec]">
                              <img
                                src={productImage(product)}
                                alt={product.name}
                                loading="lazy"
                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                              />
                              <div
                                className={cn(
                                  'absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full border transition-colors',
                                  selected
                                    ? 'border-furniture-gold bg-furniture-gold text-black'
                                    : 'border-white/70 bg-black/35 text-white'
                                )}
                              >
                                {selected ? <Check className="h-3 w-3" /> : null}
                              </div>
                            </div>
                            <div className="space-y-0.5 p-1.5">
                              <p className="line-clamp-1 text-[10px] font-semibold leading-snug">
                                {product.name}
                              </p>
                              <p className="text-[10px] text-muted-foreground">
                                {formatMoneyTn(Number(product.price) || 0)} HT
                              </p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {items.length > 0 && (
                  <div className="shrink-0 flex gap-2 overflow-x-auto border-t border-border/50 pt-3">
                    {items.map((item) => (
                      <div
                        key={item.id}
                        className="flex shrink-0 items-center gap-2 rounded-full border border-border bg-white py-1 pl-1 pr-2 shadow-soft"
                      >
                        <img
                          src={lineImage(item, products)}
                          alt=""
                          className="h-8 w-8 rounded-full object-cover"
                        />
                        <span className="max-w-[120px] truncate text-[11px] font-medium">
                          {lineTitle(item, products)}
                          {item.isCustom ? ' · perso' : ''}
                        </span>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() =>
                            setItems((prev) => prev.filter((row) => row.id !== item.id))
                          }
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {step.id === 'details' && (
              <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col gap-4 overflow-hidden">
                <div className="shrink-0 space-y-1 text-center">
                  <h3 className="font-sans text-2xl font-semibold tracking-wide">
                    Quantités & prix
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Prix catalogue par défaut — modifiables ligne par ligne.
                  </p>
                </div>

                <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
                  {items.map((item) => {
                    return (
                      <div
                        key={item.id}
                        className="grid gap-3 rounded-2xl border border-border/70 bg-white p-3 shadow-soft sm:grid-cols-[88px_1fr] sm:p-4"
                      >
                        <div className="overflow-hidden rounded-xl bg-[#f3f1ec]">
                          <img
                            src={lineImage(item, products)}
                            alt=""
                            className="aspect-square h-full w-full object-cover"
                          />
                        </div>
                        <div className="space-y-3">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-semibold leading-snug">
                              {lineTitle(item, products)}
                              {item.isCustom ? (
                                <span className="ml-2 text-[10px] font-medium uppercase tracking-wider text-black">
                                  Perso
                                </span>
                              ) : null}
                            </p>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="shrink-0 text-destructive"
                              onClick={() =>
                                setItems((prev) => prev.filter((row) => row.id !== item.id))
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                              <Label>Quantité</Label>
                              <Input
                                type="number"
                                min={1}
                                value={item.quantity}
                                onChange={(e) =>
                                  updateItem(item.id, {
                                    quantity: Math.max(1, Number(e.target.value) || 1),
                                  })
                                }
                                className="h-10"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label>Prix unitaire HT</Label>
                              <Input
                                type="number"
                                min={0}
                                step={0.001}
                                value={item.unit_price}
                                onChange={(e) =>
                                  updateItem(item.id, {
                                    unit_price: Math.max(0, Number(e.target.value) || 0),
                                  })
                                }
                                className="h-10"
                              />
                            </div>
                          </div>
                          <div className="space-y-1.5">
                            <Label>Désignation</Label>
                            <Textarea
                              rows={2}
                              value={item.designation}
                              onChange={(e) => {
                                const value = e.target.value;
                                const lines = value.split('\n');
                                updateItem(item.id, {
                                  designation: value,
                                  name: lines[0] || item.name,
                                  description: lines.slice(1).join('\n'),
                                });
                              }}
                              className="resize-none text-sm"
                            />
                          </div>
                          <p className="text-right text-xs text-muted-foreground">
                            Total ligne{' '}
                            <span className="font-semibold text-foreground">
                              {formatMoneyTn(item.quantity * item.unit_price)}
                            </span>
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {step.id === 'finalize' && (
              <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden lg:flex-row lg:gap-4">
                {/* Mobile: Modifier / Aperçu switch */}
                <div className="flex shrink-0 rounded-full border border-border bg-muted/40 p-1 lg:hidden">
                  <button
                    type="button"
                    onClick={() => setFinalizeMobileTab('edit')}
                    className={cn(
                      'flex-1 rounded-full px-3 py-2.5 text-sm font-medium transition-colors',
                      finalizeMobileTab === 'edit'
                        ? 'bg-furniture-gold text-black shadow-sm'
                        : 'text-muted-foreground'
                    )}
                  >
                    Modifier
                  </button>
                  <button
                    type="button"
                    onClick={() => setFinalizeMobileTab('preview')}
                    className={cn(
                      'flex-1 rounded-full px-3 py-2.5 text-sm font-medium transition-colors',
                      finalizeMobileTab === 'preview'
                        ? 'bg-furniture-gold text-black shadow-sm'
                        : 'text-muted-foreground'
                    )}
                  >
                    Aperçu PDF
                  </button>
                </div>

                {/* Editable side panel */}
                <aside
                  className={cn(
                    'min-h-0 w-full shrink-0 flex-col overflow-hidden rounded-2xl border border-border/70 bg-white shadow-soft lg:flex lg:max-h-none lg:w-[340px] xl:w-[380px]',
                    finalizeMobileTab === 'edit' ? 'flex flex-1' : 'hidden'
                  )}
                >
                  <div className="shrink-0 border-b border-border/60 px-4 py-3">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-black">
                      Modifier
                    </p>
                    <h3 className="font-sans text-base font-semibold">Ajustements du devis</h3>
                  </div>

                  <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="editClientName">Client</Label>
                      <Input
                        id="editClientName"
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        className="h-10"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="editClientAddress">Adresse</Label>
                      <Textarea
                        id="editClientAddress"
                        value={clientAddress}
                        onChange={(e) => setClientAddress(e.target.value)}
                        rows={2}
                        className="resize-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="editClientMatricule">Matricule F</Label>
                      <Input
                        id="editClientMatricule"
                        value={clientMatricule}
                        onChange={(e) => setClientMatricule(e.target.value)}
                        className="h-10"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="editSerial">N° de devis</Label>
                      <Input
                        id="editSerial"
                        value={serialNumber}
                        onChange={(e) => setSerialNumber(e.target.value)}
                        className="h-10 font-mono"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        {formatSerialDisplay(serialNumber || 'XXXXX-YEAR')} · {date}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="editDiscount">Remise (montant HT)</Label>
                      <Input
                        id="editDiscount"
                        type="number"
                        min={0}
                        step={0.001}
                        value={discount}
                        onChange={(e) => setDiscount(Math.max(0, Number(e.target.value) || 0))}
                        className="h-10"
                        placeholder="0.000"
                      />
                    </div>

                    <div className="space-y-2 border-t border-border/60 pt-3">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Lignes
                      </p>
                      {items.map((item) => {
                        return (
                          <div
                            key={item.id}
                            className="space-y-2 rounded-xl border border-border/60 bg-[#faf8f4] p-3"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-xs font-semibold leading-snug">
                                {lineTitle(item, products)}
                                {item.isCustom ? ' · perso' : ''}
                              </p>
                              <button
                                type="button"
                                className="text-muted-foreground hover:text-destructive"
                                onClick={() =>
                                  setItems((prev) => prev.filter((row) => row.id !== item.id))
                                }
                                aria-label="Retirer"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-1">
                                <Label className="text-[10px]">Qté</Label>
                                <Input
                                  type="number"
                                  min={1}
                                  value={item.quantity}
                                  onChange={(e) =>
                                    updateItem(item.id, {
                                      quantity: Math.max(1, Number(e.target.value) || 1),
                                    })
                                  }
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-[10px]">P.U. HT</Label>
                                <Input
                                  type="number"
                                  min={0}
                                  step={0.001}
                                  value={item.unit_price}
                                  onChange={(e) =>
                                    updateItem(item.id, {
                                      unit_price: Math.max(0, Number(e.target.value) || 0),
                                    })
                                  }
                                  className="h-9"
                                />
                              </div>
                            </div>
                            <Textarea
                              rows={2}
                              value={item.designation}
                              onChange={(e) => {
                                const value = e.target.value;
                                const lines = value.split('\n');
                                updateItem(item.id, {
                                  designation: value,
                                  name: lines[0] || item.name,
                                  description: lines.slice(1).join('\n'),
                                });
                              }}
                              className="resize-none text-xs"
                            />
                          </div>
                        );
                      })}
                    </div>

                    <div className="rounded-xl border border-border/60 bg-[#f7f4ef] p-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total HT</span>
                        <strong>{formatMoneyTn(totals.totalHt)}</strong>
                      </div>
                      <div className="mt-1 flex justify-between text-muted-foreground">
                        <span>Remise</span>
                        <span>- {formatMoneyTn(totals.discountAmount)}</span>
                      </div>
                      <div className="mt-2 flex justify-between border-t border-border/50 pt-2">
                        <span className="font-semibold">Après remise</span>
                        <strong className="text-black">
                          {formatMoneyTn(totals.totalAfterDiscount)}
                        </strong>
                      </div>
                    </div>
                  </div>
                </aside>

                {/* Live PDF preview */}
                <div
                  className={cn(
                    'min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border/70 bg-[#e8e4dc] shadow-soft lg:flex',
                    finalizeMobileTab === 'preview' ? 'flex' : 'hidden lg:flex'
                  )}
                >
                  <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border/50 bg-white/80 px-3 py-2.5 backdrop-blur sm:px-4">
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-black">
                        Aperçu PDF
                      </p>
                      <p className="hidden text-xs text-muted-foreground sm:block">
                        Mise à jour en direct — format A4
                      </p>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      className="hidden shrink-0 bg-furniture-gold text-black hover:bg-furniture-gold-hover sm:inline-flex"
                      onClick={handleDownload}
                      disabled={isDownloading}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      {isDownloading ? 'Génération…' : 'Télécharger'}
                    </Button>
                  </div>
                  <div className="min-h-0 flex-1 overflow-auto p-2 sm:p-3 md:p-5">
                    <div className="mx-auto min-h-full w-full max-w-[210mm] overflow-hidden rounded-sm bg-white shadow-[0_12px_40px_rgba(0,0,0,0.18)]">
                      <iframe
                        title="Aperçu PDF devis"
                        srcDoc={livePreviewHtml}
                        className="block h-[min(1120px,calc(100dvh-11rem))] w-full origin-top scale-[0.92] border-0 bg-white sm:h-[min(1120px,calc(100dvh-14rem))] sm:scale-100"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer nav */}
      <footer className="shrink-0 border-t border-border/60 bg-white/95 px-3 py-2.5 backdrop-blur pb-[max(0.65rem,env(safe-area-inset-bottom))] sm:px-4 sm:py-3 md:px-6">
        <div className="flex items-center justify-between gap-2 sm:gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={goBack}
            disabled={stepIndex === 0}
            className="h-11 min-w-0 px-3 sm:px-4"
          >
            <ArrowLeft className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Retour</span>
          </Button>

          <p className="text-[11px] tabular-nums text-muted-foreground sm:text-xs">
            {stepIndex + 1} / {STEPS.length}
          </p>

          {step.id !== 'finalize' ? (
            <Button
              type="button"
              onClick={goNext}
              className="h-11 bg-furniture-gold px-4 text-black hover:bg-furniture-gold-hover sm:px-5"
            >
              Continuer
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleDownload}
              disabled={isDownloading}
              className="h-11 bg-furniture-gold px-3 text-black hover:bg-furniture-gold-hover sm:px-5"
            >
              <Download className="mr-1.5 h-4 w-4 sm:mr-2" />
              <span className="sm:hidden">{isDownloading ? '…' : 'PDF'}</span>
              <span className="hidden sm:inline">
                {isDownloading ? 'Génération…' : 'Télécharger PDF'}
              </span>
            </Button>
          )}
        </div>
      </footer>

      <Dialog
        open={customOpen}
        onOpenChange={(next) => {
          setCustomOpen(next);
          if (!next) resetCustomForm();
        }}
      >
        <DialogContent className="max-h-[90dvh] max-w-[calc(100vw-1.5rem)] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Article personnel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <p className="text-sm text-muted-foreground">
              Cet article est enregistré uniquement sur ce devis — pas dans le catalogue produits.
            </p>

            <div className="space-y-2">
              <Label>Image</Label>
              <label
                className={cn(
                  'flex cursor-pointer flex-col items-center justify-center gap-2 overflow-hidden rounded-xl border border-dashed border-border bg-[#faf8f4] transition-colors hover:border-furniture-gold/50',
                  customImage ? 'p-0' : 'px-4 py-8'
                )}
              >
                {customImage ? (
                  <img src={customImage} alt="Aperçu" className="max-h-48 w-full object-cover" />
                ) : (
                  <>
                    <ImagePlus className="h-8 w-8 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Choisir 1 image</span>
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleCustomImageChange(e.target.files?.[0] ?? null)}
                />
              </label>
              {customImageName && (
                <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span className="truncate">{customImageName}</span>
                  <button
                    type="button"
                    className="shrink-0 text-destructive hover:underline"
                    onClick={() => handleCustomImageChange(null)}
                  >
                    Retirer
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="customName">Nom</Label>
              <Input
                id="customName"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="Ex. Chaise sur mesure"
                className="h-11"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="customQty">Quantité</Label>
                <Input
                  id="customQty"
                  type="number"
                  min={1}
                  value={customQty}
                  onChange={(e) => setCustomQty(Math.max(1, Number(e.target.value) || 1))}
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customPrice">Prix unitaire HT</Label>
                <Input
                  id="customPrice"
                  type="number"
                  min={0}
                  step={0.001}
                  value={customPrice}
                  onChange={(e) => setCustomPrice(Math.max(0, Number(e.target.value) || 0))}
                  className="h-11"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCustomOpen(false);
                resetCustomForm();
              }}
            >
              Annuler
            </Button>
            <Button
              className="bg-furniture-gold text-black hover:bg-furniture-gold-hover"
              onClick={handleAddCustomArticle}
            >
              Ajouter au devis
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={serialDialogOpen} onOpenChange={setSerialDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fixer le compteur de série</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Prochain format <code>XXXXX-YEAR</code> (ex. 000090-2026).
            </p>
            <div className="space-y-2">
              <Label htmlFor="nextSerial">Prochain numéro (XXXXX)</Label>
              <Input
                id="nextSerial"
                type="number"
                min={1}
                value={nextSerialInput}
                onChange={(e) => setNextSerialInput(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSerialDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveSerialSettings}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default DevisGenerator;
